import React, { useEffect } from 'react'
import { toast } from 'react-toastify'
import { Box, Grid, MenuItem, Select, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { useMutation } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import CatalogAPI from 'api/faber/CatalogAPI'
import NormalText from 'common/Typography/NormalText'
import { allCountries } from 'constants/supportedCountriesList'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

const CustomDatePicker = (props: DatePickerProps) => {
  const { label, disabled, date, setDate } = props

  const handleStartDate = (newValue: Date | null) => {
    setDate(newValue)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateTimePicker
        label={label}
        inputFormat='dd-MM-yyyy HH:00'
        views={['year', 'month', 'day', 'hours']}
        value={date}
        onChange={handleStartDate}
        renderInput={(params) => (
          <TextField placeholder='Date' variant='standard' {...params} />
        )}
        disabled={disabled}
      />
    </LocalizationProvider>
  )
}

export default CustomDatePicker

interface DatePickerProps {
  label: string
  disabled: boolean
  date: Date | null
  setDate: (value: Date | null) => void
}

const formikInitialValues = {
  packageTypeRef: '',
  vendor: 'TELNA',
  tenant: 'USA',
  name: '',
  description: '',
  callout: '',
  currency: '',
  value: '',
  symbol: '',
  dataInGB: 0,
  voiceInMins: 0,
  SMS: 0,
  validityInDays: 0,
  image: '',
  countries: '',
  packageType: 'BASE',
  showClient: true,
  redirectUrl: '',
  provisionMethodFK: '1'
}

const NewCatalogSchema = Yup.object().shape({})

const CenteredBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '80%'
})
const CenteredGrid = styled(Grid)({
  display: 'flex',
  alignItems: 'center'
})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

const startDate = new Date()
startDate.setMinutes(0)

const endDate = new Date()
endDate.setMinutes(0)

export const NewCatalogForm = ({
  handleClose
}: {
  handleClose: () => void
}) => {
  interface CatalogRow {
    id: string
    reward_name: string
    reward_description: string
    reward_description_internal: string
  }
  interface ProvisionRow {
    id: string
    method: string
    esimConfigId: string
  }

  const [packageRows, setPackageRows] = React.useState<CatalogRow[]>([])
  const [packageSelectionModel, setPackageSelectionModel] =
    React.useState<string>('')
  const [provisionMethodRows, setProvisionMethodRows] = React.useState<ProvisionRow[]>([])
  const handleUsageTypeSelect = (
    e,
    setFieldValue: (
      field: string,
      value,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    setFieldValue(e.target.name, e.target.value)
  }
  const faberToken = OrbitSession.get('faberToken')

  const { data: packageItems } = useQuery(['getTelnaPackages'], async () =>
    CatalogAPI.getAllTelnaPackages(faberToken)
  )

  const packageColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200
    },
    {
      field: 'dataUsageAllowanceInBytes',
      headerName: 'Data In Bytes',
      width: 200
    },
    {
      field: 'notes',
      headerName: 'notes',
      width: 350
    },
    {
      field: 'supportedCountries',
      headerName: 'Supported Countries',
      width: 350
    },
  ]

  const { data: provisionMethods } = useQuery(['getProvisionMethods'], async () =>
    CatalogAPI.getAllProvisionMethods(faberToken)
  )
  useEffect(() => {
    if (provisionMethods?.data) {
      setProvisionMethodRows(provisionMethods.data);
    }
  }, [provisionMethods]);

  useEffect(() => {
    if (packageItems) {
      setPackageRows(packageItems?.data)
    }
  }, [packageItems])

  const { mutate: createCatalog } = useMutation(CatalogAPI.createCatalogItem, {
    onSuccess: () => {
      toast.success('Catalog created successfully')
      handleClose()
    },
    onError: () => {
      toast.error('Failed to create catalog')
    }
  })

  const isValidCountryCode = (code: string) => {
    return code.length === 3 && Object.values(allCountries).includes(code)
  }


  const handleFormSubmit = (values: NewCatalogFormikProps) => {
    const supportedCountries = values.countries
      .split(',')
      .map((code) => code.trim())
      .filter((code) => isValidCountryCode(code))

    if (supportedCountries.length !== values.countries.split(',').length) {
      toast.error('Invalid countries')
      return
    }

    const allValues = {
      packageTypeRef: packageSelectionModel ? packageSelectionModel : '',
      vendor: values.vendor,
      tenant: values.tenant,
      name: values.name,
      description: [values.description],
      callout: values.callout,
      currency: values.currency,
      value: values.value,
      symbol: values.symbol,
      dataInGB: values.dataInGB,
      voiceInMins: values.voiceInMins,
      SMS: values.SMS,
      validityInDays: values.validityInDays,
      image: values.image,
      countries: supportedCountries,
      packageType: values.packageType,
      showClient: values.showClient,
      redirectUrl: values.redirectUrl,
      faberToken: faberToken,
      provisionMethodFKId: values.provisionMethodFKId
    }

    createCatalog(allValues)
  }

  /**
   * Event handlers
   */
  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={NewCatalogSchema}
    >
      {({
        submitForm,
        handleChange,
        errors,
        touched,
        handleBlur,
        setFieldValue
      }) => (
        <Form>
          <Grid container rowSpacing={2}>
            {/* CAMPAIGN PROMO NAME */}
            <Grid item xs={3}>
              <NormalText isBold>Name</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='name'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'name'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.name && touched.name)}
                  helperText={errors.name && touched.name ? errors.name : null}
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Description</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='description'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'description'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.description && touched.description)}
                  helperText={
                    errors.description && touched.description
                      ? errors.description
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Currency</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='currency'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'currency'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.currency && touched.currency)}
                  helperText={
                    errors.currency && touched.currency ? errors.currency : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Value</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='value'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'value'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.value && touched.value)}
                  helperText={
                    errors.value && touched.value ? errors.value : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Symbol</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='symbol'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'symbol'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.symbol && touched.symbol)}
                  helperText={
                    errors.symbol && touched.symbol ? errors.symbol : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Data In GB</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='dataInGB'
                  type='number'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'dataInGB'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.dataInGB && touched.dataInGB)}
                  helperText={
                    errors.dataInGB && touched.dataInGB ? errors.dataInGB : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Validity In Days</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='validityInDays'
                  type='number'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'validityInDays'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.validityInDays && touched.validityInDays)}
                  helperText={
                    errors.validityInDays && touched.validityInDays
                      ? errors.validityInDays
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Image URL (Link from S3)</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='image'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'image'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.image && touched.image)}
                  helperText={
                    errors.image && touched.image ? errors.image : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Countries</NormalText>
            </Grid>
            <CenteredGrid item xs={9}>
              <CenteredBox>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'countries'
                  }}
                  name='countries'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  multiline
                  variant='outlined'
                  error={!!(errors.countries && touched.countries)}
                  helperText={
                    errors.countries && touched.countries
                      ? errors.countries
                      : null
                  }
                />
              </CenteredBox>
            </CenteredGrid>
            <Grid item xs={3}>
              <NormalText isBold>Tenant</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='tenant'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'tenant'
                  }}
                  defaultValue='USA'
                  fullWidth
                  variant='outlined'
                  error={!!(errors.tenant && touched.tenant)}
                  helperText={
                    errors.tenant && touched.tenant ? errors.tenant : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Package Type</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='packageType'
                defaultValue='BASE'
              >
                <MenuItem value='BASE'>Base</MenuItem>
                <MenuItem value='BASE_UNLIMITED'>BASE_UNLIMITED</MenuItem>
                <MenuItem value='BONUS'>Bonus</MenuItem>
                <MenuItem value='PROMO'>Promo</MenuItem>
                <MenuItem value='TOPUP'>Topup</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Vendor</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='vendor'
                defaultValue='TELNA'
              >
                <MenuItem value='TELNA'>TELNA</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Provision Method</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='provisionMethodFKId'
                defaultValue={provisionMethodRows && provisionMethodRows.length > 0 ? provisionMethodRows[0].id : '1'}
              >
                {provisionMethodRows && provisionMethodRows.map((provisionMethod) => {
                  return <MenuItem key={provisionMethod.id} value={provisionMethod.id}>{provisionMethod.method}</MenuItem>
                })}
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Underlying Package</NormalText>
            </Grid>
            <Grid item xs={12} height={640}>
              <DataGrid
                rows={packageRows}
                columns={packageColumns}
                rowsPerPageOptions={[10]}
                pageSize={10}
                selectionModel={packageSelectionModel}
                getRowId={(row) => row.stringId}
                onSelectionModelChange={(newSelection) => {
                  setPackageSelectionModel(newSelection[0] as string)
                }}
              />
            </Grid>
          </Grid>
          <Grid>
            <Box mt={3}>
              {' '}
              {/* Adjust the number to increase or decrease the space */}
              <Button onClick={submitForm}>Create catalog</Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

interface NewCatalogFormikProps {
  packageTypeRef: string
  vendor: string
  tenant: string
  name: string
  description: string
  callout?: string
  currency: string
  value: string
  symbol: string
  dataInGB: number
  voiceInMins: number
  SMS: number
  validityInDays: number
  image?: string
  countries: string
  packageType: string
  showClient: boolean
  redirectUrl?: string
  provisionMethodFKId?: string
}
